import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Product, PRODUCT_CATEGORIES } from '../../types'
import { localDb, formatCurrency, calculateSMPrice } from '../../lib/db'
import { useToast } from '../../contexts/ToastContext'

export default function AdminInventory() {
    const [products, setProducts] = useState<Product[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [showAddModal, setShowAddModal] = useState(false)
    const [editingProduct, setEditingProduct] = useState<Product | null>(null)
    const { showToast } = useToast()

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: PRODUCT_CATEGORIES[0] as string,
        mrp: 0,
        sm_price: 0,
        stock: 0,
        barcode: '',
        image_url: ''
    })

    useEffect(() => {
        loadProducts()
    }, [])

    const loadProducts = () => {
        const all = localDb.products.getAll()
        setProducts(all)
    }

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.barcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
    )

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            category: PRODUCT_CATEGORIES[0],
            mrp: 0,
            sm_price: 0,
            stock: 0,
            barcode: '',
            image_url: ''
        })
        setEditingProduct(null)
    }

    const openEditModal = (product: Product) => {
        setEditingProduct(product)
        setFormData({
            name: product.name,
            description: product.description || '',
            category: product.category,
            mrp: product.mrp,
            sm_price: product.sm_price,
            stock: product.stock,
            barcode: product.barcode || '',
            image_url: product.image_url || ''
        })
        setShowAddModal(true)
    }

    const handleMRPChange = (mrp: number) => {
        const sm_price = calculateSMPrice(mrp)
        setFormData(prev => ({ ...prev, mrp, sm_price }))
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name || formData.mrp <= 0) {
            showToast('Please fill in required fields', 'error')
            return
        }

        if (editingProduct) {
            localDb.products.update(editingProduct.id, formData)
            showToast('Product updated successfully', 'success')
        } else {
            localDb.products.create(formData)
            showToast('Product added successfully', 'success')
        }

        loadProducts()
        setShowAddModal(false)
        resetForm()
    }

    const handleDelete = (product: Product) => {
        if (confirm(`Delete "${product.name}"?`)) {
            localDb.products.delete(product.id)
            loadProducts()
            showToast('Product deleted', 'success')
        }
    }

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Inventory</h1>
                    <p className="admin-page-subtitle">Manage your products</p>
                </div>
                <button className="btn btn-primary" onClick={() => { resetForm(); setShowAddModal(true) }}>
                    + Add Product
                </button>
            </div>

            {/* Search */}
            <div className="inventory-toolbar">
                <input
                    type="text"
                    className="input search-input"
                    placeholder="Search by name, barcode, or category..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
                <span className="product-count">{filteredProducts.length} products</span>
            </div>

            {/* Products Table */}
            <div className="inventory-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Category</th>
                            <th>MRP</th>
                            <th>SM Price</th>
                            <th>Stock</th>
                            <th>Barcode</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProducts.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="empty-cell">
                                    No products found
                                </td>
                            </tr>
                        ) : (
                            filteredProducts.map(product => (
                                <tr key={product.id} className={product.stock <= 5 ? 'low-stock-row' : ''}>
                                    <td>
                                        <div className="product-cell">
                                            <div className="product-thumb">
                                                {product.image_url ? (
                                                    <img src={product.image_url} alt={product.name} />
                                                ) : (
                                                    <span>👗</span>
                                                )}
                                            </div>
                                            <div>
                                                <span className="product-name">{product.name}</span>
                                                {product.ai_model_image_url && (
                                                    <span className="ai-badge">✨ AI</span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{product.category}</td>
                                    <td className="price-cell">{formatCurrency(product.mrp)}</td>
                                    <td className="price-cell sm-price">{formatCurrency(product.sm_price)}</td>
                                    <td className={product.stock <= 5 ? 'low-stock' : ''}>
                                        {product.stock}
                                        {product.stock <= 5 && <span className="low-badge">Low</span>}
                                    </td>
                                    <td><code>{product.barcode || '-'}</code></td>
                                    <td>
                                        <div className="action-buttons">
                                            <button className="btn btn-sm btn-ghost" onClick={() => openEditModal(product)}>
                                                Edit
                                            </button>
                                            <Link to={`/admin/ai-studio?product=${product.id}`} className="btn btn-sm btn-ghost">
                                                ✨ AI
                                            </Link>
                                            <button className="btn btn-sm btn-ghost btn-danger-text" onClick={() => handleDelete(product)}>
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="input-group full-width">
                                        <label className="input-label">Product Name *</label>
                                        <input
                                            type="text"
                                            className="input"
                                            value={formData.name}
                                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    <div className="input-group full-width">
                                        <label className="input-label">Description</label>
                                        <textarea
                                            className="input"
                                            rows={3}
                                            value={formData.description}
                                            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Category *</label>
                                        <select
                                            className="input"
                                            value={formData.category}
                                            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                        >
                                            {PRODUCT_CATEGORIES.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Barcode</label>
                                        <input
                                            type="text"
                                            className="input"
                                            value={formData.barcode}
                                            onChange={e => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                                            placeholder="e.g., SMF001"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">MRP (₹) *</label>
                                        <input
                                            type="number"
                                            className="input"
                                            value={formData.mrp || ''}
                                            onChange={e => handleMRPChange(Number(e.target.value))}
                                            min="0"
                                            required
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">SM Price (₹) - 10% discount</label>
                                        <input
                                            type="number"
                                            className="input"
                                            value={formData.sm_price || ''}
                                            onChange={e => setFormData(prev => ({ ...prev, sm_price: Number(e.target.value) }))}
                                            min="0"
                                        />
                                        <span className="input-hint">Auto-calculated as 10% off MRP</span>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Stock Quantity</label>
                                        <input
                                            type="number"
                                            className="input"
                                            value={formData.stock || ''}
                                            onChange={e => setFormData(prev => ({ ...prev, stock: Number(e.target.value) }))}
                                            min="0"
                                        />
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Image URL</label>
                                        <input
                                            type="url"
                                            className="input"
                                            value={formData.image_url}
                                            onChange={e => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingProduct ? 'Save Changes' : 'Add Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
        .admin-page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .inventory-toolbar {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
        }
        
        .inventory-toolbar .search-input {
          max-width: 400px;
        }
        
        .product-count {
          color: var(--text-muted);
          font-size: var(--text-sm);
        }
        
        .inventory-table-container {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          overflow: hidden;
        }
        
        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }
        
        .product-thumb {
          width: 48px;
          height: 60px;
          background: var(--bg-tertiary);
          border-radius: var(--radius-md);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .product-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .product-name {
          font-weight: 500;
        }
        
        .ai-badge {
          display: inline-block;
          margin-left: var(--space-2);
          font-size: var(--text-xs);
          color: var(--color-primary-light);
        }
        
        .price-cell {
          font-family: var(--font-display);
          font-weight: 600;
        }
        
        .sm-price {
          color: var(--color-success);
        }
        
        .low-stock {
          color: var(--color-warning);
        }
        
        .low-stock-row {
          background: rgba(245, 158, 11, 0.05);
        }
        
        .low-badge {
          display: inline-block;
          margin-left: var(--space-2);
          padding: 2px 6px;
          font-size: 10px;
          font-weight: 600;
          color: var(--color-warning);
          background: rgba(245, 158, 11, 0.2);
          border-radius: var(--radius-sm);
        }
        
        .action-buttons {
          display: flex;
          gap: var(--space-1);
        }
        
        .btn-danger-text {
          color: var(--color-error) !important;
        }
        
        .empty-cell {
          text-align: center;
          padding: var(--space-8) !important;
          color: var(--text-muted);
        }
        
        .modal-lg {
          max-width: 600px;
        }
        
        .modal-close {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--text-muted);
          background: none;
          border: none;
          cursor: pointer;
          border-radius: var(--radius-md);
        }
        
        .modal-close:hover {
          background: var(--bg-glass);
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-4);
        }
        
        .form-grid .full-width {
          grid-column: 1 / -1;
        }
        
        .input-hint {
          font-size: var(--text-xs);
          color: var(--text-muted);
          margin-top: var(--space-1);
        }
        
        select.input {
          cursor: pointer;
        }
        
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </div>
    )
}
